import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn } from "lucide-react";
import { IsaleModal } from "./Modal";
import { useTranslation } from "@/shared/i18n/I18nProvider";
import { sessionStore } from "@/shared/api/client";

/**
 * AuthExpiredModal — global UX shown when the API layer detects an
 * unrecoverable 401 (access token expired AND refresh token also dead).
 *
 * Listens to `isale:auth-expired` window event dispatched by
 * `apiRequest` (apps/isale-web/src/shared/api/client.ts L131-138) right
 * after `sessionStore.clear()`. Two layers of signal cohabit:
 *   • `isale:session-cleared` → DATA event, syncs Zustand state (see
 *     apps/isale-web/src/shared/session.ts L32).
 *   • `isale:auth-expired`    → UX event, this modal handles it.
 *
 * UX (Vincent feedback 2026-05-23 PM — upload-flow expired token
 * showed raw "Missing or malformed Authorization header" toast):
 *   - Friendly Vietnamese title + body (non-tech audience).
 *   - 5-second countdown displayed inline so the user knows what's about
 *     to happen instead of being yanked away without warning.
 *   - Two CTAs: "Đăng nhập ngay" (immediate redirect) · "Để sau" (cancel
 *     countdown, close modal — useful if user wants to copy a draft text
 *     from the open form first).
 *   - Backdrop press disabled so users can't accidentally cancel.
 *
 * Mounted once in App.tsx inside <BrowserRouter> so `useNavigate` works.
 */

const COUNTDOWN_SECONDS = 5;

export function AuthExpiredModal() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const tickRef = useRef<number | null>(null);

  // Centralized navigate-to-sign-in: also purges localStorage defensively
  // in case the apiRequest layer's `sessionStore.clear()` didn't fire
  // (e.g. a stray non-401 path triggered this modal manually).
  function goSignIn() {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    // Belt + suspenders: clear the localStorage session row AND fire the
    // session-cleared event so Zustand syncs (apps/isale-web/src/shared/
    // session.ts L32). The apiRequest path intentionally skipped this
    // dispatch (to avoid Layout racing the modal open) — we own it now.
    sessionStore.clear();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("isale:session-cleared"));
    }
    setVisible(false);
    setSecondsLeft(COUNTDOWN_SECONDS);
    navigate("/sign-in", { replace: true });
  }

  function cancelCountdown() {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    setVisible(false);
    setSecondsLeft(COUNTDOWN_SECONDS);
  }

  useEffect(() => {
    function onAuthExpired() {
      // Already on /sign-in? Don't show modal — would be redundant.
      if (window.location.pathname === "/sign-in") return;
      setSecondsLeft(COUNTDOWN_SECONDS);
      setVisible(true);
    }
    window.addEventListener("isale:auth-expired", onAuthExpired);
    return () => {
      window.removeEventListener("isale:auth-expired", onAuthExpired);
    };
  }, []);

  // Drive the countdown while modal is visible. Each tick decrements;
  // at 0, navigate to sign-in. Cleared on cancel + on unmount.
  useEffect(() => {
    if (!visible) return;
    tickRef.current = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          // Use a microtask to navigate AFTER state settles — avoids the
          // "navigate inside setState updater" React warning.
          queueMicrotask(() => goSignIn());
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      if (tickRef.current !== null) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
    // goSignIn is stable across re-renders for this component instance —
    // closing over navigate/setVisible is fine; we don't want to restart
    // the interval if those refs change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <IsaleModal
      visible={visible}
      onDismiss={cancelCountdown}
      title={t("auth.sessionExpiredTitle")}
      subtitle={t("auth.sessionExpiredSubtitle")}
      variant="warning"
      hideDismiss
      dismissOnBackdropPress={false}
      actions={[
        {
          label: t("auth.sessionExpiredLoginNowCta"),
          variant: "primary",
          onPress: goSignIn,
        },
        {
          label: t("auth.sessionExpiredLaterCta"),
          variant: "secondary",
          onPress: cancelCountdown,
        },
      ]}
    >
      <div className="flex items-start gap-3 rounded-2xl border border-subtle bg-primary-soft p-3">
        <LogIn className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-fg">
            {t("auth.sessionExpiredBody")}
          </p>
          <p
            className="mt-2 text-xs font-semibold text-fg-subtle"
            aria-live="polite"
          >
            {t("auth.sessionExpiredCountdown", { seconds: secondsLeft })}
          </p>
        </div>
      </div>
    </IsaleModal>
  );
}
